import { useMemo } from "react"
import { NpAccordion } from "src/components/np-design/components/accordion/NpAccordion"
import { NpAccordionItemData } from "src/components/np-design/components/accordion/NpAccordionItem"
import { ProductListItemImage } from "src/components/product-list/ProductListItemImage"
import { A } from "src/components/unstyled/A"
import { routes } from "src/config/routes"
import { createFaqLd } from "src/seo/faqLd"
import { LinkedData } from "src/seo/LinkedData"
import { isTruthy } from "src/utilities/typescript"
import { useProductContext } from "./context"

export const ProductAccordion = () => {
  const { dimensions, attributes, deliveryContents, accessories, parents, faqs, safetyWarnings } = useProductContext()

  const accordionItems = useMemo(() => {
    const dimensionChildren = (
      <div className="RichText">
        {dimensions?.map(({ name, value }, index) => (
          <p key={index}>
            <b>{name}</b>
            <br />
            {value}
          </p>
        ))}
      </div>
    )

    const attributesChildren = (
      <div className="RichText">
        {attributes?.map(({ name, value }, index) => (
          <p key={index}>
            <b>{name}</b>
            <br />
            {value}
          </p>
        ))}
      </div>
    )

    const deliveryContentsChildren = deliveryContents && (
      <div className="RichText" dangerouslySetInnerHTML={{ __html: deliveryContents }} />
    )

    const accessoryItems = accessories?.length ? accessories : parents?.length ? parents : undefined
    const accessoriesHeading = accessories?.length
      ? "Mit diesem Produkt ist folgendes Zubehör kompatibel:"
      : parents?.length
        ? "Dieses Zubehör ist mit folgenden Produkten kompatibel:"
        : undefined

    const accessoriesChildren = (
      <div>
        <div className="text-lg">{accessoriesHeading}</div>

        <ul className="mt-4 flex flex-col gap-3 py-4 text-lg">
          {accessoryItems?.map(({ image, name, slug }, index) => (
            <li key={index} className="group/accessory w-full">
              <A href={routes.formatProduct(slug)} className="flex w-full items-center justify-start gap-4">
                <div className="size-15">
                  <ProductListItemImage src={image} alt={name} />
                </div>

                <div className="group-hover/accessory:text-np-green flex-1 text-xl font-medium">{name}</div>
              </A>
            </li>
          ))}
        </ul>
      </div>
    )

    const faqChildren = (
      <div className="RichText">
        {faqs?.map(({ question, answer: answerHtml }, index) => (
          <div key={index}>
            <h3 className="text-xl">{question}</h3>
            <div dangerouslySetInnerHTML={{ __html: answerHtml }} />
          </div>
        ))}
      </div>
    )

    const manufacturer = (
      <div className="RichText">
        <h3 className="text-xl">Hersteller:</h3>
        elvent GmbH
        <br />
        Robert-Bosch-Str. 8<br />
        78549 Spaichingen
        <br />
        Deutschland
        <h3 className="text-xl">Kontakt:</h3>
        <A href={routes.contact}>Kontaktmöglichkeiten →</A>
      </div>
    )

    const safetyWarningContents = safetyWarnings && (
      <div className="RichText">
        <h3 className="text-xl">Sicherheitshinweise:</h3>
        <div dangerouslySetInnerHTML={{ __html: safetyWarnings }} />
      </div>
    )

    return [
      deliveryContents && {
        heading: "Lieferumfang",
        children: deliveryContentsChildren,
      },
      attributes?.length && {
        heading: "Produktdaten",
        children: attributesChildren,
      },
      dimensions?.length && {
        heading: "Abmessungen",
        children: dimensionChildren,
      },
      accessories?.length && {
        heading: "Zubehör",
        children: accessoriesChildren,
      },
      parents?.length && {
        heading: "Kompatible Produkte",
        children: accessoriesChildren,
      },
      faqs?.length && {
        heading: "FAQ",
        children: faqChildren,
      },
      {
        heading: "Herstellerinformationen",
        children: manufacturer,
      },
      safetyWarningContents && {
        heading: "Sicherheitshinweise",
        children: safetyWarningContents,
      },
    ].filter(isTruthy) satisfies NpAccordionItemData[]
  }, [dimensions, attributes, deliveryContents, accessories, parents, faqs, safetyWarnings])

  if (!accordionItems?.length) {
    return null
  }

  return (
    <>
      <NpAccordion items={accordionItems} />

      {faqs?.map(({ question, answer: answerHtml }, index) => (
        <LinkedData key={index} data={createFaqLd({ question, answer: answerHtml })} />
      ))}
    </>
  )
}
